import { Link, useNavigate, useParams, useSearchParams, useOutletContext } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react";

import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Spinner from 'react-bootstrap/Spinner'
import Stack from 'react-bootstrap/Stack'

import ConfirmationModal from "../../components/ConfirmationModal.jsx";
import DebugAccordion from "../../components/DebugAccordion"
import JsonForm from "../../components/JsonForm.jsx";
import PageTitle from "../../components/PageTitle.jsx"
import UtgNav from "./components/UtgNav"
import UtgTools from "./components/UtgTools.jsx";
import { addNotification, NotificationTypes } from "../../stores/notification-store.js";
import { isEmptyObject, makeFieldsReadonly } from '../../utils/utils'
import { UpdateComponentConfig } from "../../utils/api.js"

import useComponentConfigData, { componentConfigData } from "../../hooks/useComponentConfigData.js"
import useComponentConfigSchema, { componentConfigSchema } from "../../hooks/useComponentConfigSchema.js"
import useUtgData, { utg } from "../../hooks/useUtgData";

export default function Config() {

    const navigate = useNavigate()
    const { beId, componentId } = useParams()
    const [searchParams] = useSearchParams()
    const debug = !!searchParams.get('debug')
    const { config, accessToken, canEditSignal, accordionData: sidebarAccordionData  } = useOutletContext()

    const { loading: utgLoading } = useUtgData(beId, componentId, config, accessToken)
    const { loading: componentConfigSchemaLoading } = useComponentConfigSchema(beId, utg.value.UtgVersion, config, accessToken)
    const { loading: componentConfigLoading, modified } = useComponentConfigData(beId, utg.value, config, accessToken)

    const jsonFormData = useComputed(() => {

        if (!isEmptyObject(componentConfigSchema.value) && !isEmptyObject(componentConfigData.value)) {
        
            // deep clone the schema
            let clonedSchema = structuredClone(componentConfigSchema.value)

            // remove the api interfaces from the schema since those are edited in a separate page
            delete clonedSchema.config.properties.apiInterfaceConfig

            // disable editing of all fields if canEditSignal is false
            if (!canEditSignal.value) {
                clonedSchema = makeFieldsReadonly(clonedSchema)
            }

            // sets the variable on the global windows object so that shift-utils.js code can determine if we are working with a profile or config schema
            globalThis.SCHEMA_MODE = 'CONFIG'
            console.log('Set SCHEMA_MODE to', globalThis.SCHEMA_MODE)

            // NOTE: if onSubmitValid is changed to onSubmit then the callback must also be changed to receive both errors and values
            const formData = {
                schema: clonedSchema.config,
                value: componentConfigData.value.componentConfig.config,
                loadedComponentConfig: componentConfigData.value.componentConfig.config,
                onSubmitValid: handleFormSubmit,
                onCancel: handleCancel,
                onFormChange: handleFormChange,
                canEditConfig: canEditSignal.value
            }
            console.log('formData from config', formData)
            return formData
        }
    })

    const showCancelModal = useSignal(false)
    const isFormEdited = useSignal(false)
    const accordionData = [
        { name: 'Component Config Data', data: componentConfigData },
        { name: 'UTG Data', data: utg },
        { name: 'Component Schema', data: componentConfigSchema },
        { name: 'Access Token', data: accessToken },
        { name: 'App Config', data: config }
    ]

    const handleFormSubmit = values => {
        if (!canEditSignal.value) {
            addNotification('You are not authorized to edit this UTG.', NotificationTypes.ERROR)
            return false
        }
        try {
            console.log('values', values)
            if (values) {
                
                // build the full config data including apiInterface and deviceConfig data to save back to server
                let data = {
                    config: values,
                    hash: componentConfigData.value.componentConfig.hash,
                    isCommitted: componentConfigData.value.componentConfig.isCommitted,
                    lastUpdatedAt: componentConfigData.value.componentConfig.lastUpdatedAt,
                    lastUpdatedBy: componentConfigData.value.componentConfig.lastUpdatedBy,
                    revisionId: componentConfigData.value.componentConfig.revisionId,
                    componentId: componentConfigData.value.componentConfig.componentId
                }
                data.config.apiInterfaceConfig = componentConfigData.value.componentConfig.config.apiInterfaceConfig || {}
                data.config.apiInterfaceConfig.apiInterface = data.config.apiInterfaceConfig.apiInterface || []
                data.config.deviceConfig = componentConfigData.value.componentConfig.config.deviceConfig || {}
                data.config.deviceConfig.device = data.config.deviceConfig.device || []

                console.log(data)
                UpdateComponentConfig(beId, utg.value.MachinePermanentId, utg.value.ComponentPermanentId, data, config, accessToken)
                    .then(result => {
                        modified.value = true
                        componentConfigData.value = result
                        addNotification('Component Config was successfully updated.', NotificationTypes.SUCCESS, true)
                        navigate('../')
                    })
                    .catch(error => {
                        console.log(error.toString())
                        addNotification(`Unable to update the Component Config. ${error.toString()}`, NotificationTypes.ERROR, false)
                        return false
                    })
                    .finally(() => {
                        // must return false to avoid postback
                        return false
                    })
            }
        }
        catch (error) {
            console.log(error)
        }

        // return false to prevent form submit from refreshing the page
        return false
    }

    const handleCancel = () => {
        if (isFormEdited.value) {
            showCancelModal.value = true
        }
        else {
            navigate('../')
        }
    }

    const handleFormChange = () => {
        isFormEdited.value = true
    }

    if (utgLoading.value || componentConfigLoading.value || componentConfigSchemaLoading.value) {
        return (
            <div className="d-flex justify-content-center">
                <Spinner className="mt-5 align-center" animation="border" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
            </div>
        )
    }

    return (
        <>
        <ConfirmationModal show={showCancelModal.value} handleCancel={() => showCancelModal.value = false} handleConfirm={() => navigate('../')} message={'You have unsaved changes to this form. Are you sure you want to leave and lose these changes?'} title={"Warning: Unsaved Changes"} cancelText="Continue Editing" confirmText="Lose Changes" />
        <Breadcrumb className="px-3 pt-1 bg-light">
            <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/utgs` }}>
                UTG List
            </Breadcrumb.Item>
            <Breadcrumb.Item active>Manage {utg.value.ComponentNickname}</Breadcrumb.Item>
        </Breadcrumb>
        <Stack direction="horizontal" gap={3}>
            <PageTitle title='UTG Configuration' canEditSignal={canEditSignal} />
            <UtgTools componentConfigData={componentConfigData.value}></UtgTools>
        </Stack>
        <UtgNav beId={beId} componentId={componentId} activeKey="4"></UtgNav>
        <JsonForm data={jsonFormData.value}></JsonForm>
        <DebugAccordion data={accordionData} debug={debug} />
        </>
    )
}